// The three booklets, at the top of every Genie's activation page.
//
// Same three for every Genie, built the moment the page opens, straight off
// the Genie's live record. Nothing to configure, nothing to wait for, nothing
// stored — so a renamed Genie or a changed URL can never leave a stale PDF on
// a shelf pointing at a dead page.
//
// The cover you see is the real first page of the real document, rendered in
// the same isolated iframe the print goes through. There is no separate
// thumbnail that can drift out of step with what comes out of the printer.

import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, Download, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { ActivationFunnelService } from '@/services/analytics/ActivationFunnelService';
import {
  buildPackDocument,
  PACK_DOCUMENTS,
  resolvePackPrintKit,
  type BuiltDocument,
  type PackDocumentId,
  type PackDocumentMeta,
} from '@/services/activation/print/packDocuments';
import { ActivationPackWithTarget } from '@/types/activation/activation-pack';
import type { PackKit } from '@/types/activation/packKit';
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

/** A4 at 96dpi — the width the document lays itself out at inside the frame. */
const SHEET_PX = 794;
/** One A4 page at the same density. The cover is clipped to exactly this. */
const PAGE_PX = 1123;
/**
 * The document's on-screen body padding (6mm), which sits above the first
 * sheet. The frame is pulled up by it so the cover starts flush with the card
 * instead of behind a strip of the preview's grey.
 */
const SCREEN_PAD_PX = Math.round((6 / 25.4) * 96);

interface BookletState {
  built: BuiltDocument | null;
  error: string | null;
}

const EMPTY_STATE: BookletState = { built: null, error: null };

interface PackBookletsProps {
  pack: ActivationPackWithTarget;
  brand: string;
}

export const PackBooklets = ({ pack, brand }: PackBookletsProps) => {
  const [kit, setKit] = useState<PackKit | null>(() => resolvePackPrintKit(pack));
  const [states, setStates] = useState<Record<PackDocumentId, BookletState>>({
    pack: EMPTY_STATE,
    posters: EMPTY_STATE,
    stickers: EMPTY_STATE,
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setKit(resolvePackPrintKit(pack));
  }, [pack]);

  // Build all three on open, one after another. They share the QR encoder and
  // each is ~30 pages of codes; running them together is slower overall and
  // makes the page stutter while someone is trying to read it.
  useEffect(() => {
    if (!kit) return;
    let live = true;
    setStates({ pack: EMPTY_STATE, posters: EMPTY_STATE, stickers: EMPTY_STATE });

    void (async () => {
      for (const meta of PACK_DOCUMENTS) {
        if (!live) return;
        try {
          const built = await buildPackDocument(meta.id, kit);
          if (!live) return;
          setStates((prev) => ({ ...prev, [meta.id]: { built, error: null } }));
        } catch (buildError) {
          console.error(`Failed to build the ${meta.title.toLowerCase()}:`, buildError);
          if (!live) return;
          setStates((prev) => ({
            ...prev,
            [meta.id]: {
              built: null,
              error:
                buildError instanceof Error
                  ? buildError.message
                  : 'This booklet could not be built.',
            },
          }));
        }
      }
    })();

    return () => {
      live = false;
    };
  }, [kit, attempt]);

  // No published page means every code in the booklet would point at the
  // marketing site. A wall of QR codes that scans wrong is the one failure
  // that cannot be recalled once it is on a door, so nothing is offered.
  if (!kit) {
    return (
      <div className="mb-4 lg:mb-6 rounded-2xl border border-gray-200 bg-white px-5 py-4 md:px-6 md:py-5">
        <div className="flex items-start gap-3">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" />
          <div>
            <h3 className="font-semibold text-gray-900">Publish your Genie's page first</h3>
            <p className="mt-0.5 text-sm text-gray-600">
              Every code in a printed booklet points at your Genie's live page. Give this Genie a
              page address and the three booklets appear here, ready to print.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4 lg:mb-6">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-bold text-gray-900 md:text-xl">Your three booklets</h2>
        <p className="text-sm text-gray-600">
          Print-ready for {kit.name}. Every code points at{' '}
          <span className="break-all font-medium text-gray-800">{kit.url}</span>
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
        {PACK_DOCUMENTS.map((meta, index) => (
          <BookletCard
            key={meta.id}
            index={index + 1}
            meta={meta}
            state={states[meta.id]}
            kit={kit}
            pack={pack}
            brand={brand}
            onRetry={() => setAttempt((value) => value + 1)}
          />
        ))}
      </div>
    </div>
  );
};

interface BookletCardProps {
  index: number;
  meta: PackDocumentMeta;
  state: BookletState;
  kit: PackKit;
  pack: ActivationPackWithTarget;
  brand: string;
  onRetry: () => void;
}

const BookletCard = ({ index, meta, state, kit, pack, brand, onRetry }: BookletCardProps) => {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const coverRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.35);
  const { built, error } = state;

  // Scale off the real card width. A fixed zoom fits one monitor and clips the next.
  useEffect(() => {
    const cover = coverRef.current;
    if (!cover) return;
    const measure = () => setScale(cover.clientWidth / SHEET_PX);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(cover);
    return () => observer.disconnect();
  }, [built]);

  /**
   * Prints the booklet's own frame. The document carries its own `@page` rules
   * at A4, so "Save as PDF" in the print dialog gives one exact file — and
   * every code stays vector, which a rasterised export cannot promise.
   */
  const download = useCallback(() => {
    const frame = frameRef.current;
    if (!frame?.contentWindow) return;
    frame.contentWindow.focus();
    frame.contentWindow.print();
    toast.success(`${meta.title} is ready`, {
      description: "Choose 'Save as PDF' as the destination to keep the file.",
    });
    ActivationFunnelService.packShared(
      ActivationFunnelService.packGenieId(pack),
      'download',
      { asset_type: `booklet_${meta.id}` },
    );
  }, [meta.id, meta.title, pack]);

  /** Opens the whole booklet full size, for flicking through before printing. */
  const openFullSize = useCallback(() => {
    if (!built) return;
    const blob = new Blob([built.html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    // Revoked late: revoking before the new tab has read the blob shows a
    // blank page in Safari and Firefox.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }, [built]);

  return (
    <div className="p-[1.5px] rounded-2xl bg-gradient-to-br from-[#4E9CFF] to-[#FF4DB8]">
      <div className="flex h-full flex-col overflow-hidden rounded-[14px] bg-white">
        {/* Cover — the real first page, and the frame the print goes through */}
        <div
          ref={coverRef}
          className="relative w-full overflow-hidden border-b border-gray-100 bg-[#EEF1F6]"
          style={{ height: PAGE_PX * scale }}
        >
          <span
            className="absolute left-3 top-3 z-10 flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-white shadow"
            style={{ background: `linear-gradient(135deg, ${brand}, #FF4DB8)` }}
          >
            {index}
          </span>

          {built ? (
            <iframe
              ref={frameRef}
              title={`${meta.title} — ${kit.name}`}
              srcDoc={built.html}
              tabIndex={-1}
              // The frame holds the whole booklet but shows one page, so it
              // would otherwise draw its own scrollbars across the cover. The
              // document itself is left untouched — it is the print target.
              scrolling="no"
              className="pointer-events-none"
              style={{
                width: SHEET_PX,
                height: PAGE_PX + SCREEN_PAD_PX,
                border: 0,
                // Scale first, then lift the body padding out of view. With a
                // top-left origin the translate is applied in pre-scale units.
                transform: `scale(${scale}) translateY(-${SCREEN_PAD_PX}px)`,
                transformOrigin: 'top left',
              }}
            />
          ) : error ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center">
              <AlertCircle className="h-6 w-6 text-red-500" />
              <p className="text-xs text-red-600">{error}</p>
            </div>
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2">
              <GenieIcon name="files" size={32} color={brand} label="" />
              <span className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Building…
              </span>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex flex-1 flex-col gap-3 p-4 md:p-5">
          <div className="flex-1">
            <h3 className="font-bold text-gray-900">{meta.title}</h3>
            <p className="mt-1 text-sm leading-5 text-gray-600">{meta.blurb}</p>
            <p className="mt-2 text-xs text-gray-500">{meta.contains}</p>

            {built && (
              <p className="mt-2 text-xs font-medium" style={{ color: brand }}>
                {built.pages} pages · A4
              </p>
            )}

            {/*
              A long Genie address pushes the smallest pieces under the printed
              module floor. Those sheets are dropped rather than failing the
              build, and this says which, so nobody goes looking for them.
            */}
            {built && built.dropped.length > 0 && (
              <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] leading-4 text-amber-900">
                <span className="font-semibold">Left out:</span> {built.dropped.join(', ')} — at
                those sizes this Genie's address prints too small to scan.
              </p>
            )}
          </div>

          {error ? (
            <button
              onClick={onRetry}
              className="flex items-center justify-center gap-2 rounded-xl border-2 px-4 py-2.5 text-sm font-semibold transition-colors hover:bg-gray-50"
              style={{ borderColor: brand, color: brand }}
            >
              <RefreshCw className="h-4 w-4" />
              Try again
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={download}
                disabled={!built}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow transition-all hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:shadow"
                style={{ background: `linear-gradient(135deg, ${brand}, #FF4DB8)` }}
              >
                {built ? <Download className="h-4 w-4" /> : <Loader2 className="h-4 w-4 animate-spin" />}
                {built ? 'Download PDF' : 'Building…'}
              </button>
              <button
                onClick={openFullSize}
                disabled={!built}
                title="Open the whole booklet full size"
                className="flex items-center justify-center gap-1.5 rounded-xl border-2 px-3 py-2.5 text-sm font-semibold transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ borderColor: brand, color: brand }}
              >
                <ExternalLink className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
